import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { string, number, bool, func } from 'prop-types';
import Dropzone from '#component/shipment/form/documents/dropzone';
import { WARNING } from '#action/types';
import {
  FileUploadCnt,
  UploadButton,
  DataTypeDescription
} from './styles';
import { uuidv4 } from '#helper';

/**
 * Renders file upload button and Dropzone.
 * Performs processing files according to type, size and number of files.
 * Dispatches warning if number of files or size is more than allowed.
 * @returns {React.ReactFragment}
 */

const FileUpload = props => {
  const {
    accept = 'image/*,.pdf',
    maxFiles = 10,
    maxSize = 1,
    disabled = false,
    uploadedLength = 0,
    handleUpload = () => null
  } = props;
  const dispatch = useDispatch();

  const dataTypeDescription = useMemo(() => {
    const formatStr = accept.split(',').reduce((a, c) => {
      const matched = c.match(/((\.)?([a-z]))*(\/[0-9a-z]{1,}){0,}/i);
      if (matched?.[0]?.length > 0) {
        return a.length > 0 ? `${a}, ${matched[0]}` : matched[0];
      }
      return a;
    }, '');

    return `(Maximum size ${maxSize} MB, up to ${maxFiles} files${formatStr.length > 0 ? `, ${formatStr}` : ''})`;
  }, [accept, maxFiles, maxSize]);

  const uploadFiles = event => {
    const [
      filesToAdd,
      oversizedFileNames,
      extraFileNames
    ] = Array.from(event.target?.files || event.dataTransfer?.files).reduce((a, file) => {
      if (file.size < maxSize * 1024 * 1024) {
        if (uploadedLength + a[0].length + 1 <= maxFiles) {
          a[0].push(file);
        } else {
          a[2].push(file.name);
        }
      } else {
        a[1].push(file.name);
      }

      return a;
    }, [[], [], []]);

    let message = '';

    if (oversizedFileNames.length > 0) {
      message += `Current file${oversizedFileNames.length > 1 ? 's' : ''}: ${oversizedFileNames.join(', ')} couldn't be uploaded. Please choose file${oversizedFileNames.length > 1 ? 's' : ''} up to ${maxSize} MB`;
    }
    if (extraFileNames.length > 0) {
      message += `${message.length > 0 ? '. ' : ''}Current file${extraFileNames.length > 1 ? 's' : ''}: ${extraFileNames.join(', ')} couldn't be uploaded. You can upload up to ${maxFiles} files`;
    }

    if (message.length > 0) {
      dispatch({
        type: WARNING,
        payload: {
          message
        }
      });
    }

    handleUpload(filesToAdd.map(f => {
      const extension = f.name.match(/[^.]+$/i)[0];

      return ({
        id: uuidv4(),
        name: f.name.slice(0, f.name.length - extension.length - 1),
        extension,
        description: '',
        file: f
      });
    }));
  };

  return (
    <>
      <FileUploadCnt>
        <UploadButton
          variant="contained"
          component="label"
          disabled={disabled}
        >
          Choose File
          <input
            onChange={uploadFiles}
            accept={accept}
            multiple
            type="file"
            disabled={disabled}
            hidden
          />
        </UploadButton>
        <DataTypeDescription component="span" variant="body2">
          {dataTypeDescription}
        </DataTypeDescription>
      </FileUploadCnt>
      <Dropzone
        handleDrop={uploadFiles}
        accept={accept}
        disabled={disabled}
      />
    </>
  );
};

FileUpload.propTypes = {
  accept: string, // Comma separated file extensions or valid MIME type
  maxFiles: number,
  maxSize: number, // Maximum file size in MB
  disabled: bool,
  uploadedLength: number, // Total number of files in form
  handleUpload: func
};

export default FileUpload;
